/**
 * Generic "Filters" button + portaled popover for checkbox and multi-select filters.
 *
 * Filter state lives in the host module and is passed in via props. The popover renders
 * through a portal to avoid being clipped by scrollable or overflow-hidden containers.
 *
 * Prefer {@link props.sections} when the panel needs labeled groups. The flat
 * `checkboxes` / `selects` / `footerCheckboxes` props remain for simpler panels.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Filter } from "../../icons/svg/outline";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { SearchableMultiSelect } from "../forms";

/**
 * @typedef {Object} FilterPanelCheckbox
 * @property {string} id Stable key for the checkbox row.
 * @property {string} label Checkbox label text.
 * @property {boolean} checked Current checked state.
 * @property {(checked: boolean) => void} onChange Called with the new checked state.
 * @property {string} [title] Optional tooltip text.
 * @property {import("react").ReactNode} [expandContent] Optional content shown under the
 *   checkbox when checked (smooth height expand).
 */

/**
 * @typedef {Object} FilterPanelSelect
 * @property {string} id Stable key for the select row.
 * @property {string} label Field label above the multi-select.
 * @property {Array<string>} value Selected option values.
 * @property {Array<{ value: string, label: string }>} options Available options.
 * @property {(values: Array<string>) => void} onChange Called with the new selected values.
 * @property {string} [placeholder] Search input placeholder while options remain.
 * @property {string} [emptyPlaceholder] Placeholder when nothing is selected.
 */

/**
 * @typedef {Object} FilterPanelDateRange
 * @property {string} id Stable key for the date-range row.
 * @property {string} label Section label above the start/end inputs.
 * @property {string} startValue Start date (`YYYY-MM-DD` or empty).
 * @property {string} endValue End date (`YYYY-MM-DD` or empty).
 * @property {(value: string) => void} onStartChange Called with the new start date.
 * @property {(value: string) => void} onEndChange Called with the new end date.
 * @property {string} [startLabel="Start"] Label for the start input.
 * @property {string} [endLabel="End"] Label for the end input.
 */

/**
 * @typedef {Object} FilterPanelSection
 * @property {string} id Stable section key.
 * @property {string} [title] Optional subheader above the section body.
 * @property {Array<FilterPanelCheckbox>} [checkboxes] Checkboxes at the top of the section.
 * @property {Array<FilterPanelDateRange>} [dateRanges] Date ranges after checkboxes.
 * @property {Array<FilterPanelSelect>} [selects] Multi-selects after date ranges.
 * @property {Array<FilterPanelCheckbox>} [footerCheckboxes] Checkboxes after selects.
 */

/**
 * Render one checkbox row, optionally with expandable content when checked.
 *
 * @param {Object} props
 * @param {FilterPanelCheckbox} props.checkbox Checkbox config.
 * @returns {JSX.Element}
 */
function FilterCheckboxRow({ checkbox }) {
  return (
    <div className="mb-3 last:mb-0">
      <label
        className="flex caret-transparent cursor-pointer items-start gap-2 text-sm text-gray-700"
        title={checkbox.title}
      >
        <input
          type="checkbox"
          className="mt-0.5"
          checked={checkbox.checked}
          onChange={(event) => checkbox.onChange(event.target.checked)}
        />
        <span>{checkbox.label}</span>
      </label>
      {checkbox.expandContent != null ? (
        <div
          className={`grid transition-[grid-template-rows] duration-200 ease-out ${
            checkbox.checked ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <div className="pt-2 pl-2">{checkbox.expandContent}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Render one labeled filter section (checkboxes, date ranges, selects).
 *
 * @param {Object} props
 * @param {FilterPanelSection} props.section Section config.
 * @param {boolean} props.showTopBorder Whether to draw a divider above this section.
 * @returns {JSX.Element|null}
 */
function FilterPanelSectionBody({ section, showTopBorder }) {
  const checkboxes = section.checkboxes ?? [];
  const dateRanges = section.dateRanges ?? [];
  const selects = section.selects ?? [];
  const footerCheckboxes = section.footerCheckboxes ?? [];
  const hasBody =
    checkboxes.length > 0 ||
    dateRanges.length > 0 ||
    selects.length > 0 ||
    footerCheckboxes.length > 0;
  if (!hasBody && !section.title) {
    return null;
  }

  return (
    <div
      className={
        showTopBorder ? "mt-3 border-t border-gray-100 pt-3" : undefined
      }
    >
      {section.title ? (
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {section.title}
        </div>
      ) : null}

      {checkboxes.map((checkbox) => (
        <FilterCheckboxRow key={checkbox.id} checkbox={checkbox} />
      ))}

      {dateRanges.length ? (
        <div
          className={`space-y-3 ${checkboxes.length ? "mt-3 pt-0" : ""}`}
        >
          {dateRanges.map((range) => (
            <div key={range.id} className="space-y-2">
              <div className="text-sm font-medium text-gray-700">
                {range.label}
              </div>
              <label className="block text-xs text-gray-600">
                {range.startLabel || "Start"}
                <input
                  type="date"
                  value={range.startValue}
                  max={range.endValue || undefined}
                  onChange={(event) => range.onStartChange(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100"
                />
              </label>
              <label className="block text-xs text-gray-600">
                {range.endLabel || "End"}
                <input
                  type="date"
                  value={range.endValue}
                  min={range.startValue || undefined}
                  onChange={(event) => range.onEndChange(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100"
                />
              </label>
            </div>
          ))}
        </div>
      ) : null}

      {selects.length ? (
        <div
          className={`space-y-3 ${
            checkboxes.length || dateRanges.length ? "mt-3" : ""
          }`}
        >
          {selects.map((select) => (
            <SearchableMultiSelect
              key={select.id}
              label={select.label}
              value={select.value}
              options={select.options}
              onChange={select.onChange}
              placeholder={select.placeholder}
              emptyPlaceholder={select.emptyPlaceholder}
            />
          ))}
        </div>
      ) : null}

      {footerCheckboxes.length ? (
        <div
          className={
            checkboxes.length || dateRanges.length || selects.length
              ? "mt-3"
              : undefined
          }
        >
          {footerCheckboxes.map((checkbox) => (
            <FilterCheckboxRow key={checkbox.id} checkbox={checkbox} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Generic filters button and popover panel.
 *
 * @param {Object} props - Component props.
 * @param {Array<FilterPanelSection>} [props.sections=[]] Optional labeled sections
 *   (preferred for longer panels). When non-empty, flat checkbox/select props are ignored.
 * @param {Array<FilterPanelCheckbox>} [props.checkboxes=[]] Checkbox toggles shown at the top of the panel.
 * @param {Array<FilterPanelDateRange>} [props.dateRanges=[]] Start/end date filters shown below checkboxes.
 * @param {Array<FilterPanelSelect>} [props.selects=[]] Multi-select filters shown below the checkboxes.
 * @param {Array<FilterPanelCheckbox>} [props.footerCheckboxes=[]] Checkbox toggles shown below selects.
 * @param {boolean} props.isActive Whether any filter is non-default.
 * @param {() => void} props.onClearAll Resets every filter to its default.
 * @param {string} [props.ariaLabel="Filters"] Accessible label for the popover dialog.
 * @param {boolean} [props.iconOnly=false] Render the trigger as an icon-only button.
 * @param {"toolbar"|"header"} [props.variant="toolbar"] Visual size for the trigger button.
 * @returns {JSX.Element}
 */
export default function FiltersPanel({
  sections = [],
  checkboxes = [],
  dateRanges = [],
  selects = [],
  footerCheckboxes = [],
  isActive,
  onClearAll,
  ariaLabel = "Filters",
  iconOnly = false,
  variant = "toolbar",
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, right: 0 });
  const isHeaderVariant = variant === "header";
  const rootClassName = isHeaderVariant
    ? "relative flex h-8 shrink-0 items-center"
    : "relative flex h-6 shrink-0 items-center";
  const buttonSizeClassName = isHeaderVariant
    ? "h-8 rounded-md px-3 text-xs font-semibold shadow-sm"
    : `h-6 rounded-md text-xs font-medium shadow-sm ${iconOnly ? "px-2" : "px-3"}`;
  const iconClassName = isHeaderVariant ? "h-4 w-4 shrink-0" : "h-4 w-4 shrink-0";

  const resolvedSections = useMemo(() => {
    if (sections.length > 0) {
      return sections;
    }
    return [
      {
        id: "default",
        checkboxes,
        dateRanges,
        selects,
        footerCheckboxes,
      },
    ];
  }, [sections, checkboxes, dateRanges, selects, footerCheckboxes]);

  const closePanel = useCallback(() => setFiltersOpen(false), []);
  useOutsideClick(panelRef, closePanel, {
    ignoreSelector:
      "[data-searchable-multi-select-listbox], [data-filters-panel], [data-planning-filters-panel]",
  });

  const updatePanelPosition = useCallback(() => {
    if (!panelRef.current) {
      return;
    }
    const rect = panelRef.current.getBoundingClientRect();
    setPanelPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!filtersOpen) {
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener("scroll", updatePanelPosition, true);
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      window.removeEventListener("scroll", updatePanelPosition, true);
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [filtersOpen, updatePanelPosition]);

  return (
    <div ref={panelRef} className={rootClassName}>
      <button
        type="button"
        className={`inline-flex items-center gap-2 border focus:outline-none focus:ring-2 focus:ring-teal-900 focus:ring-offset-1 ${buttonSizeClassName} ${
          filtersOpen || isActive
            ? "border-teal-600 bg-teal-600 text-white"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        }`}
        aria-expanded={filtersOpen}
        aria-haspopup="true"
        aria-label="Filters"
        title={iconOnly ? "Filters" : undefined}
        onClick={() => setFiltersOpen((open) => !open)}
      >
        <Filter className={iconClassName} aria-hidden />
        {iconOnly ? null : "Filters"}
      </button>

      {filtersOpen
        ? createPortal(
            <div
              data-filters-panel
              className="fixed z-50 max-h-[80vh] w-72 overflow-y-auto rounded-lg border border-white bg-white p-4 font-inter shadow-lg"
              style={{
                top: `${panelPosition.top}px`,
                right: `${panelPosition.right}px`,
              }}
              role="dialog"
              aria-label={ariaLabel}
            >
              {resolvedSections.map((section, index) => (
                <FilterPanelSectionBody
                  key={section.id}
                  section={section}
                  showTopBorder={index > 0}
                />
              ))}

              <button
                type="button"
                className="mt-4 w-full rounded-md border border-gray-300 bg-teal-600 px-3 py-0.5 text-sm font-medium text-white shadow-sm hover:bg-teal-900 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onClearAll}
                disabled={!isActive}
              >
                Clear All Filters
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
